/**
 * Ryan O'Dowd
 * 2021-04-16
 * © Copyright 2021 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import 'react-native-get-random-values';
import {
  appleAuth,
  appleAuthAndroid,
} from './AppleAuth';
import AuthButton from '@containers/Auth/AuthButton';
import {
  Platform,
} from 'react-native';
import React from 'react';
import fb from '@utils/firebase';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';
import {
  v4 as uuid,
} from 'uuid';

const AppleAuthButton = (props: AppleAuthButtonProps) => {
  const navigation = hooks.useNavigation();
  const i18n = hooks.useI18n(props.localeId);

  return (
    <AuthButton
      iconName='apple'
      providerName='Apple'
      localeId={props.localeId}
      authIsPending={props.authIsPending}
      setAuthIsPending={props.setAuthIsPending}
      isLinking={props.isLinking}
      onPress={async () => {
        if (Platform.OS === 'web') {
          fb.signInWithPopup(fb.auth, fb.AppleAuthProvider).then((__result) => {
            // @TODO: props.onClose();
          }).catch((__error) => {
            props.setAuthIsPending(false);
          });

          return;
        }

        try {
          if (Platform.OS === 'ios') {
            const response = await appleAuth.performRequest({
              requestedOperation: appleAuth.Operation.LOGIN,
              requestedScopes: [appleAuth.Scope.FULL_NAME, appleAuth.Scope.EMAIL],
            });

            // Ensure Apple returned a user identityToken
            if (!response.identityToken) {
              props.setErrorMessage(i18n('unknownError'));
              // @TODO: throw 'Apple Sign-In failed - no identify token returned';
            }

            // Create a Firebase credential from the response
            // @ts-ignore // @TODO: yeet
            const credential = fb.AppleAuthProvider.credential(response.identityToken, response.nonce); // @TODO: pretty sure this nonce is supposed to be here, but typescript is telling me no... (difference between web and native i believe)

            if (props.isLinking) {
              utilities.linkUserAndCredential(fb.auth.currentUser, credential, navigation);
            } else {
              const userCredential = await fb.signInWithCredential(fb.auth, credential);
              const fullName = [response.fullName.givenName, response.fullName.familyName].filter((n) => n).join(' ');
              if (fullName) { // empty string on subsequent logins
                utilities.updateDisplayName(userCredential.user, fullName);
              }
              fb.logEvent(fb.analytics, 'login', {
                displayName: fullName, // @TODO: this will always be null after first log in
                authType: 'apple',
              });
            }
          } else { // this is android or web
            const nonce = uuid();
            const state = uuid();

            appleAuthAndroid.configure({
              clientId: `${globals.bundleId}.client`, // The Service ID you registered with Apple
              redirectUri: globals.appleRedirectUri, // Return URL added to your Apple dev console. We intercept this redirect, but it must still match the URL you provided to Apple. It can be an empty route on your backend as it's never called.
              responseType: appleAuthAndroid.ResponseType.ALL, // The type of response requested - code, id_token, or both.
              scope: appleAuthAndroid.Scope.ALL, // The amount of user information requested from Apple.
              nonce, // Random nonce value that will be SHA256 hashed before sending to Apple.
              state, // Unique state value used to prevent CSRF attacks. A UUID will be generated if nothing is provided.
            });

            // Open the browser window for user sign in
            const response: any = await appleAuthAndroid.signIn();

            // Send the authorization code to your backend for verification
            if (response.state === state) {
              // @ts-ignore // @TODO: yeet
              const credential = fb.AppleAuthProvider.credential(response.id_token, nonce); // @TODO: pretty sure this nonce is supposed to be here, but typescript is telling me no... (difference between web and native i believe)
              if (props.isLinking) {
                utilities.linkUserAndCredential(fb.auth.currentUser, credential, navigation);
              } else {
                const userCredential = await fb.signInWithCredential(fb.auth, credential);
                const fullName = [response.fullName.givenName, response.fullName.familyName].filter((n) => n).join(' ');
                if (fullName) { // null on subsequent logins
                  utilities.updateDisplayName(userCredential.user, fullName);
                }
                fb.logEvent(fb.analytics, 'login', {
                displayName: fullName, // @TODO: this will always be null after first log in
                  authType: 'apple',
                });
              }
            }
          }
        } catch (e) {
          switch (e.code) {
            case 'auth/network-request-failed': {
              props.setErrorMessage(i18n('networkError'));
              break;
            }
            case '1001': { // user canceled sign-in flow
              break;
            }
            case 'auth/provider-already-linked': {
              props.setErrorMessage(i18n('alreadyLinkedError'));
              break;
            }
            case 'auth/popup-closed-by-user': {
              break;
            }
            default: {
              // e.code 1000 is unknown error from apple...catch that and anything else
              props.setErrorMessage(i18n('unknownError'));
              fb.recordError(fb.crashlytics, e);
              break;
            }
          }
          props.setAuthIsPending(false);
        }
      }}
    />
  );
};

class AppleAuthButtonProps {
  setErrorMessage: (errorMessage: string) => void;
  localeId: any;
  setAuthIsPending: (authIsPending: boolean) => void;

  authIsPending?: boolean;
  isLinking?: boolean;
};

export default AppleAuthButton;
